import React, { useEffect, useRef } from 'react';
import { Clock, Check, CheckCheck, X, Trash2 } from 'lucide-react';

const SEEN_TEAL = '#008C8C';

const dayNames = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  ar: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
};

const monthNames = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  ar: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
};

const formatDateDetails = (timestamp, language = 'en') => {
  if (!timestamp) return null;

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return null;

  const lang = language === 'ar' ? 'ar' : 'en';
  const day = dayNames[lang][date.getDay()];
  const month = monthNames[lang][date.getMonth()];
  const dateNum = date.getDate();
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;

  return {
    date: `${dateNum} ${month} ${year}`,
    time: timeStr,
    day,
  };
};

const getStatusConfig = (status, t) => {
  switch (status) {
    case 'sending':
      return {
        label: t.sending,
        color: '#9ca3af',
        icon: <Clock size={14} />,
      };
    case 'sent':
      return {
        label: t.sent,
        color: '#94a3b8',
        icon: <Check size={14} />,
      };
    case 'delivered':
      return {
        label: t.delivered,
        color: '#64748b',
        icon: <CheckCheck size={14} />,
      };
    case 'read':
    case 'seen':
      return {
        label: t.seen || t.read,
        color: SEEN_TEAL,
        icon: <CheckCheck size={14} />,
      };
    default:
      return {
        label: t.sent,
        color: '#94a3b8',
        icon: <Check size={14} />,
      };
  }
};

const MessageInfoPopover = ({ message, isOpen, onClose, c, t, isRTL, language, isDeletable, onDeleteMessage }) => {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const rawTimestamp = message.timestamp || message.createdAt || message.raw?.created_at || message.raw?.createdAt || message.raw?.time;
  const sentDetails = formatDateDetails(rawTimestamp, language);
  
  const rawSeenTimestamp = message.readAt || message.seenAt || message.raw?.read_at || message.raw?.seen_at || message.raw?.updated_at;
  const seenDetails = (message.status === 'read' || message.status === 'seen')
    ? formatDateDetails(rawSeenTimestamp || rawTimestamp, language) // fallback to sent time if seen time missing
    : null;
  const statusConfig = getStatusConfig(message.status, t);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(3px)',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
      onClick={onClose}
    >
      <div
        ref={popoverRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: '360px',
          background: c.popoverBg || c.sidebarBg,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${c.popoverBorder || c.border}`,
          borderRadius: '20px',
          padding: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
          animation: 'fadeInScale 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '14px',
          paddingBottom: '10px',
          borderBottom: `1px solid ${c.border}`,
        }}
      >
        <span
          style={{
            fontSize: '13px',
            fontWeight: '700',
            color: c.text,
            letterSpacing: '0.3px',
          }}
        >
          {t.messageDetails}
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: c.textSecondary,
            transition: 'color 0.15s ease',
          }}
          aria-label="Close"
        >
          <X size={14} />
        </button>
      </div>

      {/* Status */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '11px', color: c.textSecondary, marginBottom: '4px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {t.status}
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '20px',
            background: `${statusConfig.color}18`,
            color: statusConfig.color,
            fontSize: '12px',
            fontWeight: '600',
          }}
        >
          {statusConfig.icon}
          {statusConfig.label}
        </div>
      </div>

      {/* Date details */}
      {sentDetails && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: c.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t.dateSent}
            </span>
            <span style={{ fontSize: '13px', color: c.text, fontWeight: '500' }}>
              {sentDetails.date}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: c.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t.timeSent}
            </span>
            <span style={{ fontSize: '13px', color: c.text, fontWeight: '500' }}>
              {sentDetails.time}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: c.textSecondary, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {t.daySent}
            </span>
            <span style={{ fontSize: '13px', color: c.text, fontWeight: '500' }}>
              {sentDetails.day}
            </span>
          </div>

          {seenDetails && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '4px',
                paddingTop: '8px',
                borderTop: `1px solid ${c.border}`,
              }}
            >
              <span style={{ fontSize: '11px', color: SEEN_TEAL, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {t.seenAt}
              </span>
              <span style={{ fontSize: '13px', color: SEEN_TEAL, fontWeight: '600' }}>
                {seenDetails.time}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Delete Action — hidden when the delete handler is feature-gated away */}
      {isDeletable && !!onDeleteMessage && (
        <button
          onClick={() => {
            onDeleteMessage && onDeleteMessage(message);
            onClose();
          }}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '16px',
            padding: '10px',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'all 0.2s ease',
          }}
        >
          <Trash2 size={16} />
          {isRTL ? 'حذف الرسالة' : 'Delete Message'}
        </button>
      )}
      </div>
    </div>
  );
};

export default MessageInfoPopover;
